import { useMutation } from '@apollo/client';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { addToCockpitListQueryCache } from '../experiments/Cockpit/components/CockpitPanel/components/ThreadList/ThreadList.helper';
import { SvbrgAgentId, SvbrgChatSessionVariant, SvbrgLanguage } from '@/generated/gql/graphql';
import { i18n } from '~/common/i18n';
import { I18nNamespace } from '~/common/types';

import { INTAKE_LIST_NEW_SESSION } from './IntakeCreateMutation.gql';
import { INTAKE_CREATE_THREAD } from './IntakeCreateThreadMutation.gql';

interface UseIntakeCreateProps {
  formCallback?: (chatSessionId: string) => void;
  threadCallback?: (threadId: string, initialMessage?: string) => void;
}
export const useIntakeCreate = ({ formCallback = () => {}, threadCallback = () => {} }: UseIntakeCreateProps = {}) => {
  const { t: tChat } = useTranslation(I18nNamespace.intake, { keyPrefix: 'Chat' });

  const [create] = useMutation(INTAKE_LIST_NEW_SESSION);
  const [createThread, { loading: isCreatingThread, client }] = useMutation(INTAKE_CREATE_THREAD);

  const { onError } = useCustomSnackbar();

  const onClick = useCallback(
    async (variant: SvbrgChatSessionVariant) => {
      const result = await create({
        variables: {
          data: {
            variant,
          },
        },
      });
      const chatSessionId = result.data?.sevenbridges.chatSession.create.id;
      addToCockpitListQueryCache(client.cache, chatSessionId);

      formCallback(chatSessionId);
    },
    [client, create, formCallback],
  );

  const language = i18n.language === 'dx' ? SvbrgLanguage.DE : (i18n.language.toUpperCase() as SvbrgLanguage);
  const onClickNewThread = useCallback(
    async (agentId: SvbrgAgentId, initialMessage?: string) => {
      try {
        // create a new thread with the given agentId
        const result = await createThread({
          variables: {
            data: {
              title: 'New Thread',
              agentId,
              language,
            },
          },
        });
        const threadId = result.data?.sevenbridges.thread.create.id;

        if (!threadId) {
          throw new Error('Thread could not be created!');
        }

        // use a query to start the conversation (either initial message or the agent starter message)
        const starterMessage = initialMessage || tChat(`agents.${agentId}.starterMessage`);

        addToCockpitListQueryCache(client.cache, threadId, starterMessage);

        // pass starter message to the chat navigation
        threadCallback(threadId, starterMessage);
      } catch (error) {
        onError(error);
      }
    },
    [createThread, language, tChat, client, threadCallback, onError],
  );

  return {
    onClick,
    onClickNewThread,
    isCreatingThread,
  };
};
